<template>
  <div
    class="header-autocomplete rounded-sm"
    :class="{ 'header-autocomplete--active': isMenuActive || searchTerm || xs }"
  >
    <v-combobox
      ref="headerAutoCompleteElement"
      :bg-color="isMenuActive ? '#432CA014' : '#FFFFFF'"
      :class="isMenuActive || searchTerm ? 'active-border' : 'border'"
      clearable
      :menu-props="{
        contentClass: 'header-autocomplete-menu rounded',
        maxHeight: mdAndDown
          ? 'calc(100vh - 56px - 80px)'
          : 'calc(100vh - 56px - 24px)',
      }"
      :items="autocompleteResults"
      :model-value="searchTerm"
      :search="searchTerm"
      prepend-inner-icon="ico-sm-search"
      no-filter
      flat
      hide-details
      density="compact"
      variant="solo"
      item-title="text"
      return-object
      data-cy="searchAutocompleteV2"
      :placeholder="t('autocomplete.placeholder')"
      persistent-placeholder
      @update:search="(v) => searchTerm = v"
      @update:model-value="handleModelValueUpdate"
      @keydown.enter="handleEnter"
      @update:menu="(v) => (isMenuActive = v)"
      @click:clear="handleCancel"
      @focus="isMenuActive = true; trackFocus()"
    >
      <template #item="{ item, props }">
        <v-divider v-if="item.raw.divider" />
        <v-list-subheader
          v-else-if="item.raw.header"
          class="px-6 text-uppercase text-caption font-weight-medium text-medium-emphasis"
        >
          {{ item.raw.header }}
        </v-list-subheader>
        <v-list-item
          v-else
          class="list-item-height px-6 d-flex align-center"
          v-bind="props"
        >
          <template #title="{ title }">
            <div class="pointer text-subtitle-2 font-weight-regular text-high-emphasis">
              <SMHighlightWords
                v-if="searchTerm && title.length"
                :data-cy="`search-suggestion-${title.replace(' ', '-')}`"
                :text="title"
                :query="searchTerm"
                :highlightNonMatches="true"
              />
              <span v-else>{{ title }}</span>
            </div>
          </template>
          <template #prepend>
            <v-avatar
              v-if="item.raw.value && item.raw.value.image"
              :size="24"
              class="mr-1"
            >
              <v-img :src="item.raw.value.image" />
            </v-avatar>
            <v-icon
              v-else
              :size="24"
              class="mr-1"
            >
              ico-sm-search
            </v-icon>
          </template>
        </v-list-item>
      </template>
      <template #append-inner>
        <SMSpinner
          v-if="evaluating"
          size="small"
        />
      </template>
      <template
        #no-data
      >
        <SMAlternativeQueries
          v-if="alternativeQueries.length"
          :alternativeQueries="alternativeQueries"
          @onSelect="(value) => { onSelect(value); blurInput(); }"
        />
      </template>
      <template #prepend-item>
        <div
          v-if="!hasSearchTerm"
          class="d-flex justify-space-between px-6 pt-2"
        >
          <span class="text-uppercase text-caption text-secondary font-weight-medium">{{ t("header.auto-complete.recent") }}</span>
          <span
            v-if="recentSearches.length > 0"
            class="pointer text-body-2 text-secondary text-decoration-underline"
            @click.stop.prevent="dialog = true"
          >{{ t("header.auto-complete.clear.button") }}</span>
        </div>
      </template>
      <template #append-item>
        <EmtpyRecentSearches v-if="!recentSearches.length && !hasSearchTerm" />
        <SearchTip
          v-if="!searchTerm"
          class="mt-4"
        />
        <SearchShortcuts class="d-none d-md-flex" />
      </template>
    </v-combobox>
    <SMConfirmationDialog
      v-model="dialog"
      :title="t('header.auto-complete.clear.recent')"
      :body="t('header.auto-complete.clear.dialog.text')"
      :cancelButtonLabel="t('header.auto-complete.clear.dialog.cancel')"
      :confirmationButtonLabel="
        t('header.auto-complete.clear.dialog.clear-history')
      "
      @confirm="clearRecentSearches"
      @update:model-value="(v) => { dialog = v; focusInput() }"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  nextTick,
  computed,
  toRefs,
  onMounted
} from 'vue'
import { useRouter } from 'vue-router'
import { IAutocompleteItemModel } from '@src/types/autocomplete.types'
import { useI18n } from '@src/composables/useI18n'
import SMSpinner from '@src/ui/SMSpinner/index.vue'
import SMHighlightWords from '@src/ui/SMHighlightWords/index.vue'
import SearchTip from '@src/ui/SMAutoCompleteV2/components/SearchTip.vue'
import SearchShortcuts from '@src/ui/SMAutoCompleteV2/components/SearchShortcuts.vue'
import EmtpyRecentSearches from '@src/ui/SMAutoCompleteV2/components/EmtpyRecentSearches.vue'
import { useDisplay } from 'vuetify'
import SMConfirmationDialog from '@src/ui/SMConfirmationDialog/index.vue'
import useAutoComplete from './composables/useAutoComplete'
import { useRecentAutocompleteSearch } from './composables/useRecentAutocompleteSearch'
import { useAlternativeQueriesSearch } from './composables/useAlternativeQueriesSearch'
import SMAlternativeQueries from './components/SMAlternativeQueries.vue'

export default defineComponent({
  name: 'SMAutoCompleteV2',
  components: {
    SMConfirmationDialog,
    EmtpyRecentSearches,
    SearchShortcuts,
    SearchTip,
    SMSpinner,
    SMHighlightWords,
    SMAlternativeQueries
  },
  props: {
    autoFocus: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { autoFocus } = toRefs(props)
    const { t } = useI18n()
    const router = useRouter()
    const hasSearchTerm = computed(() => (searchTerm.value !== null && searchTerm.value.length > 1))
    const hasSearchResults = computed(() => (searchResults?.value.length > 0))
    const autocompleteResults = computed(() => (hasSearchTerm.value ? searchResults?.value : recentSearches.value))
    const {
      searchTerm,
      searchResults,
      evaluating,
      selectedAutocompleteResult,
      onSelect,
      clearInput,
      trackFocus
    } = useAutoComplete()
    const dialog = ref(false)
    const { mdAndDown, xs } = useDisplay()

    const { recentSearches, clearRecentSearches } = useRecentAutocompleteSearch(hasSearchTerm)
    const { alternativeQueries } = useAlternativeQueriesSearch(hasSearchResults, searchTerm)

    const headerAutoCompleteElement = ref<HTMLElement & { isMenuActive: boolean } | null>(null)
    const isMenuActive = ref(false)
    const wasItemSelectedFromList = ref(false)

    function handleModelValueUpdate(v: IAutocompleteItemModel | string | null) {
      if (v && typeof v === 'object') {
        wasItemSelectedFromList.value = true
        onSelect(v)
        blurInput()
        nextTick(() => { wasItemSelectedFromList.value = false })
      }
    }

    function handleEnter() {
      if (!wasItemSelectedFromList.value) {
        onSelect(searchTerm.value)
        blurInput()
      }
    }

    async function handleCancel(e: Event) {
      e.stopPropagation()
      clearInput()
      blurInput()
      router.push({ name: 'stream' })
      if (xs.value) {
        emit('click:back')
      }
    }

    function blurInput() {
      if (headerAutoCompleteElement.value) {
        nextTick(() => headerAutoCompleteElement?.value?.blur())
      }
    }
    function focusInput() {
      if (headerAutoCompleteElement?.value) {
        nextTick(() => headerAutoCompleteElement.value?.focus())
        headerAutoCompleteElement.value.isMenuActive = true
      }
    }

    onMounted(() => {
      if (autoFocus.value) {
        focusInput()
      }
    })

    return {
      t,
      evaluating,
      searchTerm,
      autocompleteResults,
      selectedAutocompleteResult,
      headerAutoCompleteElement,
      recentSearches,
      searchResults,
      alternativeQueries,
      hasSearchTerm,
      isMenuActive,
      onSelect,
      handleModelValueUpdate,
      handleEnter,
      handleCancel,
      trackFocus,
      focusInput,
      blurInput,
      clearRecentSearches,
      mdAndDown,
      xs,
      dialog
    }
  }
})
</script>
<style lang="scss" scoped>
@use "~@styles/constants" as consts;

.border {
  border-width: 0 !important;

  :deep(.v-field) {
    border: 2px solid #0000003d;
    border-radius: 4px;
    box-shadow: none !important;
    outline: none;
  }

  :deep(.v-field__outline) {
    display: none;
  }
}
.active-border {
  :deep(.v-field) {
    border: 2px solid #432ca0;
    border-radius: 4px;
    box-shadow: none !important;
    outline: none;
  }
}
.header-autocomplete {
  :deep(.v-input__slot) {
    padding-right: 24px !important;
    padding-left: 24px !important;
  }

  @media (min-width: map.get(vars.$grid-breakpoints, "sm")) {
    width: 320px;
  }

  @media (min-width: map.get(vars.$grid-breakpoints, "md")) {
    width: 262px;
  }

  @media (min-width: map.get(vars.$grid-breakpoints, "lg")) {
    width: 240px;
  }

  @media (min-width: map.get(vars.$grid-breakpoints, "xl")) {
    width: 320px;
  }
}

.header-autocomplete--active {
  width: 100% !important;
}

.header-autocomplete-menu .v-list {
  margin-top: 2px !important;
}

.list-item-height {
  height: 48px;
}

:deep(.v-combobox__menu-icon) {
  display: none;
}

.header-autocomplete-menu {
  .v-list-item::after {
    display: none !important;
  }
}

</style>
